/**
 * SelectedBookingSidebar Component
 *
 * A yellow sidebar panel showing booking details when a booking card is selected.
 * Displays employee/customer name, date, availability status, and a list of bookings
 * with edit/delete actions.
 *
 * Ported from Vue SelectedCardModal.vue
 *
 * @example
 * ```tsx
 * <SelectedBookingSidebar
 *   card={selectedCard}
 *   onClose={() => setSelectedCard(null)}
 *   onEditBooking={handleEdit}
 *   onDeleteBooking={handleDelete}
 * />
 * ```
 */

import React, { useMemo } from "react";
import classNames from "classnames";
import { Icon } from "@/components/ui/display/Icon";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { SumBadge } from "@/components/booking/display/SumBadge";
import {
  formatLongDate,
  formatDateTime,
  formatBookingHours,
} from "@/utils/date";
import { getBookingStatus } from "@/utils/booking/status";
import type { IBooking } from "@/services/types";
import { BookingType } from "@/services/types";
import { formatEmployeeName } from "@/hooks/data/dataQuality";
import type { SelectedBookingSidebarProps } from "./types";
import styles from "./SelectedBookingSidebar.module.css";

/**
 * Get employee display name
 */
function getEmployeeName(
  employee: {
    given_name?: string;
    surname?: string;
    name?: string;
    email?: string;
  } | null,
): string {
  if (!employee) return "Ukjent ansatt";
  return formatEmployeeName(employee);
}

/**
 * Get customer display name
 */
function getCustomerName(customer: { name: string } | null): string {
  if (!customer) return "Ukjent";
  return customer.name;
}

/**
 * Check if booking is an absence
 * Uses b_type field as the source of truth (ABSENCE = 3)
 */
function isAbsence(booking: IBooking): boolean {
  // Primary check: booking type is ABSENCE (3)
  if (booking.b_type === BookingType.ABSENCE) return true;
  // Fallback: project name is "Fravær" (legacy check)
  return booking.project?.name === "Fravær";
}

/**
 * Get project display string for a booking
 */
function getProjectDisplay(booking: IBooking): string | null {
  // Check if absence first
  if (isAbsence(booking)) {
    return null; // Return null to show absence styling instead
  }

  if (!booking.project) return "Ukjent prosjekt";

  return `Prosjekt: ${booking.project.code} - ${booking.project.name}`;
}

/**
 * Get booker name for "last modified by" text
 */
function getBookerName(booking: IBooking): string {
  if (!booking.booked_by?.id) return "ukjent person";
  return formatEmployeeName(booking.booked_by);
}

/**
 * Check if booking is a soft booking
 * Uses b_type field as the source of truth (SOFT_BOOKING = 2)
 */
function isSoftBooking(booking: IBooking): boolean {
  return booking.b_type === BookingType.SOFT_BOOKING;
}

/**
 * Calculate total hours from bookings
 *
 * Returns separate totals for:
 * - projectHours: Hard booking hours that count toward daily limit
 * - absenceHours: Absence hours
 * - softBookingHours: Soft booking hours (do NOT count toward daily limit)
 *
 * Business rule: Soft bookings should NOT trigger overbooking warnings
 */
function calculateTotalHours(bookings: IBooking[]): {
  projectHours: number;
  absenceHours: number;
  softBookingHours: number;
} {
  let projectHours = 0;
  let absenceHours = 0;
  let softBookingHours = 0;

  for (const booking of bookings) {
    if (isAbsence(booking)) {
      absenceHours += booking.hours;
    } else if (isSoftBooking(booking)) {
      // Soft bookings don't count toward daily limit
      softBookingHours += booking.hours;
    } else {
      // Hard bookings count toward daily limit
      projectHours += booking.hours;
    }
  }

  return { projectHours, absenceHours, softBookingHours };
}

/**
 * Calculate availability badge props for SumBadge
 * Returns label, hours value, and status for the badge
 */
function getAvailabilityBadgeProps(
  projectHours: number,
  absenceHours: number,
  dayHours: number,
): { label: string; hours: number } {
  const totalUsed = projectHours + absenceHours;
  const remaining = dayHours - totalUsed;

  if (remaining < 0) {
    return { label: "Overbooka:", hours: Math.abs(remaining) };
  }
  return { label: "Ledig:", hours: remaining };
}

export const SelectedBookingSidebar: React.FC<SelectedBookingSidebarProps> = ({
  card,
  hoursInDay,
  onSelectProject,
  onSelectEmployee,
  onSelectCustomer,
  onEditBooking,
  onDeleteBooking,
  className,
  "data-testid": testId,
}) => {
  // Calculate hours and status
  const { projectHours, absenceHours, softBookingHours } = useMemo(
    () => calculateTotalHours(card.bookings),
    [card.bookings],
  );

  // Check if this is a soft booking view (only soft bookings, no hard bookings)
  const isSoftBookingView = softBookingHours > 0 && projectHours === 0;

  // For soft bookings, use "booked" status (no overbooking concept)
  // For hard bookings, calculate status normally (Bug #5 fix: use org hoursInDay)
  const status = useMemo(
    () =>
      isSoftBookingView
        ? "booked"
        : getBookingStatus(projectHours, absenceHours, hoursInDay),
    [isSoftBookingView, projectHours, absenceHours, hoursInDay],
  );

  // Get badge props for SumBadge (label and hours value) (Bug #5 fix: use org hoursInDay)
  const badgeProps = useMemo(
    () => getAvailabilityBadgeProps(projectHours, absenceHours, hoursInDay),
    [projectHours, absenceHours, hoursInDay],
  );

  // Entity name and click handler
  // For customers, prefix with "Kunde:" to match Vue behavior
  const entityName = card.employee
    ? getEmployeeName(card.employee)
    : `Kunde: ${getCustomerName(card.customer)}`;

  const handleEntityClick = () => {
    if (card.employee && onSelectEmployee) {
      onSelectEmployee(card.employee);
    } else if (card.customer && onSelectCustomer) {
      onSelectCustomer(card.customer);
    }
  };

  // All bookings (visible + excluded)
  const allBookings = useMemo(
    () => [...card.bookings, ...card.excludedBookings],
    [card.bookings, card.excludedBookings],
  );

  return (
    <div
      className={classNames(styles.container, className)}
      data-testid={testId}
    >
      {/* Header */}
      <div className={styles.header}>Bookingoversikt for</div>

      {/* Entity name (clickable) */}
      <div
        className={styles.name}
        onClick={handleEntityClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleEntityClick();
          }
        }}
      >
        {entityName}
      </div>

      {/* Date */}
      <div className={styles.date}>{formatLongDate(card.date)}</div>

      {/* Availability badge - hidden for soft bookings (no capacity concept) */}
      {!isSoftBookingView && (
        <SumBadge
          hours={badgeProps.hours}
          status={status}
          label={badgeProps.label}
          size="md"
          className={styles.available}
        />
      )}

      {/* Bookings list */}
      {allBookings.length > 0 ? (
        <div className={styles.bookingList}>
          {allBookings.map((booking) => (
            <div key={booking.id} className={styles.bookingItem}>
              {/* Header row: hours + edit/delete tools */}
              <div className={styles.bookingHeader}>
                <span className={styles.hours}>
                  {formatBookingHours(booking.hours)}
                </span>
                <div className={styles.tools}>
                  <IconButton
                    icon={<Icon name="pencil" size="md" aria-hidden />}
                    variant="default"
                    size="sm"
                    onClick={() => onEditBooking?.(booking)}
                    aria-label={`Rediger booking: ${formatBookingHours(booking.hours)}`}
                    className={styles.editButton}
                    data-testid={
                      testId ? `${testId}-edit-${booking.id}` : undefined
                    }
                  />
                  <IconButton
                    icon={<Icon name="delete" size="md" aria-hidden />}
                    variant="default"
                    size="sm"
                    onClick={() => onDeleteBooking?.(booking)}
                    aria-label={`Slett booking: ${formatBookingHours(booking.hours)}`}
                    className={styles.deleteButton}
                    data-testid={
                      testId ? `${testId}-delete-${booking.id}` : undefined
                    }
                  />
                </div>
              </div>

              {/* Booking details - full width */}
              <div className={styles.bookingContent}>
                {/* Project or absence */}
                {isAbsence(booking) ? (
                  <div className={styles.bookingRow}>
                    <span className={styles.absence}>Fravær</span>
                  </div>
                ) : (
                  <>
                    {booking.project && (
                      <div className={styles.bookingRow}>
                        <span
                          className={styles.project}
                          onClick={() => onSelectProject?.(booking.project!)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onSelectProject?.(booking.project!);
                            }
                          }}
                        >
                          {getProjectDisplay(booking)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Employee (for soft bookings - shows which employee is assigned to customer) */}
                {isSoftBooking(booking) && booking.employee && (
                  <div className={styles.bookingRow}>
                    <span
                      className={styles.employee}
                      onClick={() => onSelectEmployee?.(booking.employee!)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectEmployee?.(booking.employee!);
                        }
                      }}
                    >
                      Ansatt: {booking.employee.given_name}{" "}
                      {booking.employee.surname}
                    </span>
                  </div>
                )}

                {/* Customer (for soft bookings) */}
                {booking.customer && (
                  <div className={styles.bookingRow}>
                    <span
                      className={styles.customer}
                      onClick={() => onSelectCustomer?.(booking.customer!)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectCustomer?.(booking.customer!);
                        }
                      }}
                    >
                      Kunde: {booking.customer.name}
                    </span>
                  </div>
                )}

                {/* Last modified info */}
                <div className={styles.modifiedInfo}>
                  Sist endret av {getBookerName(booking)},<br />
                  {formatDateTime(booking.updated_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.noBookings}>Ingen bookinger.</p>
      )}
    </div>
  );
};

SelectedBookingSidebar.displayName = "SelectedBookingSidebar";

export default SelectedBookingSidebar;
